import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, Clock, CheckCircle2, XCircle, AlertTriangle,
  Search, Loader2, ArrowRight, User, Building2, FileText,
  Truck, Users, Fingerprint, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: 'pending_review', label: 'Pending Review', icon: Clock, tone: 'amber' },
  { id: 'approved', label: 'Approved', icon: CheckCircle2, tone: 'emerald' },
  { id: 'rejected', label: 'Rejected', icon: XCircle, tone: 'rose' },
  { id: 'in_progress', label: 'In Progress', icon: Fingerprint, tone: 'slate' },
  { id: 'all', label: 'All', icon: Users, tone: 'blue' },
];

const toneCls = {
  amber:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  rose:    'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  slate:   'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  blue:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

const riskChip = (score) => {
  const s = score || 0;
  if (s < 30) return { text: `LOW · ${s}`, cls: 'bg-emerald-100 text-emerald-700' };
  if (s < 70) return { text: `REVIEW · ${s}`, cls: 'bg-amber-100 text-amber-700' };
  return { text: `HIGH · ${s}`, cls: 'bg-rose-100 text-rose-700' };
};

const StatusBadge = ({ status }) => {
  const map = {
    approved:       { text: 'APPROVED', cls: toneCls.emerald },
    pending_review: { text: 'PENDING REVIEW', cls: toneCls.amber },
    rejected:       { text: 'REJECTED', cls: toneCls.rose },
    in_progress:    { text: 'IN PROGRESS', cls: toneCls.slate },
    not_started:    { text: 'NOT STARTED', cls: toneCls.slate },
  };
  const m = map[status] || map.not_started;
  return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${m.cls}`}>{m.text}</span>;
};

export default function AdminKysQueuePage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('pending_review');
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/onboarding/kys/admin/queue?status_filter=${tab}&page_size=100`, { headers });
      const data = await r.json();
      setRecords(data.records || []);
      setCounts(data.counts || {});
    } finally { setLoading(false); }
  }, [tab, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDetail = async (userId) => {
    setSelectedUserId(userId);
    setDetail(null); setNotes(''); setRejectReason(''); setBanner(null);
    try {
      const r = await fetch(`${API}/api/onboarding/kys/admin/${userId}`, { headers });
      if (r.ok) setDetail(await r.json());
      else setBanner({ type: 'err', text: (await r.json()).detail || 'Failed to load' });
    } catch (e) { setBanner({ type: 'err', text: String(e) }); }
  };

  const closeDetail = () => { setSelectedUserId(null); setDetail(null); };

  const doApprove = async () => {
    setBusy(true); setBanner(null);
    try {
      const r = await fetch(`${API}/api/onboarding/kys/admin/${selectedUserId}/approve`, {
        method: 'POST', headers, body: JSON.stringify({ reviewer_notes: notes || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Approve failed');
      setBanner({ type: 'ok', text: `Approved. ShipStation: ${data.shipstation_provision?.ok ? `account ${data.shipstation_provision.account_id} provisioned (${data.shipstation_provision.mode})` : (data.shipstation_provision?.reason || 'skipped')}` });
      await refresh();
      setTimeout(closeDetail, 1500);
    } catch (e) { setBanner({ type: 'err', text: String(e.message || e) }); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { setBanner({ type: 'err', text: 'A rejection reason is required.' }); return; }
    setBusy(true); setBanner(null);
    try {
      const r = await fetch(`${API}/api/onboarding/kys/admin/${selectedUserId}/reject`, {
        method: 'POST', headers, body: JSON.stringify({ reviewer_notes: notes || null, reason: rejectReason }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Reject failed');
      setBanner({ type: 'ok', text: `Rejected: ${data.reason}` });
      await refresh();
      setTimeout(closeDetail, 1500);
    } catch (e) { setBanner({ type: 'err', text: String(e.message || e) }); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    if (!q) return records;
    const s = q.toLowerCase();
    return records.filter(r =>
      (r.merchant_email || '').toLowerCase().includes(s) ||
      (r.company?.legal_name || '').toLowerCase().includes(s) ||
      (r.merchant_company_hint || '').toLowerCase().includes(s) ||
      (r.user_id || '').toLowerCase().includes(s)
    );
  }, [records, q]);

  return (
    <div className="space-y-6" data-testid="kys-queue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">KYS Review Queue</h1>
            <p className="text-sm text-slate-500">Approve or reject merchant verification submissions. Approvals automatically provision a ShipStation sub-account.</p>
          </div>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="queue-refresh"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="queue-tabs">
        {STATUS_TABS.map(t => {
          const I = t.icon;
          const active = tab === t.id;
          const count = counts[t.id];
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${active ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'}`}
              data-testid={`queue-tab-${t.id}`}>
              <I className="w-4 h-4" />
              {t.label}
              {typeof count === 'number' && <span className={`px-1.5 py-0.5 rounded text-xs ${active ? 'bg-white/20' : toneCls[t.tone]}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search email, company, or user ID"
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
          data-testid="queue-search" />
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Merchant</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Risk</th>
                <th className="px-4 py-3 text-left">Volume / Max value</th>
                <th className="px-4 py-3 text-left">Flags</th>
                <th className="px-4 py-3 text-left">Submitted</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="queue-rows">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No records in this bucket.</td></tr>
              )}
              {filtered.map(r => {
                const risk = riskChip(r.risk_score);
                const flags = [];
                if (r.owners?.ofac_hits?.length) flags.push({ text: 'OFAC HIT', cls: toneCls.rose });
                if (r.shipper_profile?.ships_restricted_items) flags.push({ text: 'RESTRICTED', cls: toneCls.amber });
                if (r.shipper_profile?.ships_internationally) flags.push({ text: 'INTL', cls: toneCls.blue });
                if (r.fraud_summary?.mocked) flags.push({ text: 'MOCKED SCAN', cls: toneCls.slate });
                return (
                  <tr key={r.user_id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`queue-row-${r.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.company?.legal_name || r.merchant_company_hint || '—'}</div>
                      <div className="text-xs text-slate-500">{r.merchant_email || r.user_id.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-semibold ${risk.cls}`}>{risk.text}</span></td>
                    <td className="px-4 py-3 text-xs">
                      <div>{r.shipper_profile?.monthly_shipment_volume || '—'}</div>
                      <div className="text-slate-500">${r.shipper_profile?.max_shipment_value?.toLocaleString() || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {flags.length === 0 && <span className="text-xs text-slate-400">—</span>}
                        {flags.map((f, i) => <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${f.cls}`}>{f.text}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : new Date(r.updated_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(r.user_id)} data-testid={`queue-open-${r.user_id}`}>
                        Review <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selectedUserId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-end" onClick={closeDetail} data-testid="kys-detail-drawer">
          <div className="bg-white dark:bg-slate-900 w-full max-w-3xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            {!detail ? (
              <div className="p-12 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
            ) : (
              <div className="p-6 space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={detail.kys.status} />
                      {detail.kys.risk_score !== undefined && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${riskChip(detail.kys.risk_score).cls}`}>{riskChip(detail.kys.risk_score).text}</span>
                      )}
                    </div>
                    <h2 className="text-xl font-semibold">{detail.kys.company?.legal_name || detail.user.company_name || '—'}</h2>
                    <p className="text-sm text-slate-500">{detail.user.email} · {detail.kys.company?.entity_type?.replaceAll('_', ' ')}</p>
                  </div>
                  <button onClick={closeDetail} className="text-slate-400 hover:text-slate-700" data-testid="detail-close">
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {banner && (
                  <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="detail-banner">
                    {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
                    <span>{banner.text}</span>
                  </div>
                )}

                <Section icon={Building2} title="Company">
                  <KV k="Legal name" v={detail.kys.company?.legal_name} />
                  <KV k="DBA" v={detail.kys.company?.dba_name} />
                  <KV k="EIN" v={detail.kys.company?.ein} />
                  <KV k="Entity" v={detail.kys.company?.entity_type} />
                  <KV k="Formed" v={`${detail.kys.company?.date_of_formation || '?'} in ${detail.kys.company?.state_of_formation || '?'}`} />
                  <KV k="Address" v={detail.kys.company?.business_address ? `${detail.kys.company.business_address.street}, ${detail.kys.company.business_address.city}, ${detail.kys.company.business_address.state} ${detail.kys.company.business_address.zip}` : '—'} />
                  <KV k="Phone / Email" v={`${detail.kys.company?.business_phone || '—'} · ${detail.kys.company?.business_email || '—'}`} />
                </Section>

                <Section icon={FileText} title="Tax form">
                  <KV k="Form" v={detail.kys.tax?.form_type?.toUpperCase()} />
                  <KV k="Taxpayer" v={detail.kys.tax?.taxpayer_name} />
                  <KV k="TIN" v={detail.kys.tax?.tin_last4 ? `····${detail.kys.tax.tin_last4}` : '—'} />
                  <KV k="Signed" v={detail.kys.tax?.signed_at?.slice(0, 19)} />
                  <KV k="Signer" v={`${detail.kys.tax?.signature_full_name || '—'} · ${detail.kys.tax?.signature_title || ''}`} />
                </Section>

                <Section icon={Users} title="Beneficial owners">
                  <KV k="Control person" v={detail.kys.owners?.control_person_full_name} />
                  <KV k="OFAC hits" v={detail.kys.owners?.ofac_hits?.length ? detail.kys.owners.ofac_hits.join(', ') : 'None (clear)'} />
                  <div className="space-y-2 mt-2">
                    {(detail.kys.owners?.owners || []).map((o, i) => (
                      <div key={i} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm">
                        <div className="font-medium">{o.full_name} · {o.ownership_percent}%</div>
                        <div className="text-xs text-slate-500">DOB {o.date_of_birth} · SSN ····{o.ssn_last4} · {o.citizenship}{o.politically_exposed_person ? ' · PEP' : ''}</div>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section icon={User} title="Officer identity (KYC)">
                  <KV k="Name" v={detail.kys.identity?.full_name} />
                  <KV k="DOB" v={detail.kys.identity?.date_of_birth} />
                  <KV k="ID" v={`${detail.kys.identity?.id_type?.replaceAll('_', ' ')} ····${detail.kys.identity?.id_number_last4 || '?'} (${detail.kys.identity?.id_issuing_state || '?'})`} />
                  <KV k="Expires" v={detail.kys.identity?.id_expiration} />
                  <KV k="Media ID" v={detail.kys.identity?.media_id} />
                </Section>

                <Section icon={FileText} title={`Corporate documents (${detail.corporate_documents?.length || 0})`}>
                  {detail.corporate_documents?.length ? detail.corporate_documents.map(d => (
                    <div key={d.id} className="text-sm flex items-center justify-between py-1">
                      <span>{d.doc_type?.replaceAll('_', ' ')}</span>
                      <span className="text-xs text-slate-500">{d.filename}</span>
                    </div>
                  )) : <p className="text-sm text-slate-400">None uploaded.</p>}
                </Section>

                <Section icon={Truck} title="Shipper profile">
                  <KV k="Volume" v={detail.kys.shipper_profile?.monthly_shipment_volume} />
                  <KV k="Avg / Max" v={`$${detail.kys.shipper_profile?.average_shipment_value || 0} / $${detail.kys.shipper_profile?.max_shipment_value || 0}`} />
                  <KV k="Categories" v={detail.kys.shipper_profile?.primary_product_categories?.join(', ')} />
                  <KV k="Intl / Restricted" v={`${detail.kys.shipper_profile?.ships_internationally ? 'Yes' : 'No'} / ${detail.kys.shipper_profile?.ships_restricted_items ? detail.kys.shipper_profile.restricted_item_categories?.join(', ') || 'Yes' : 'No'}`} />
                  <KV k="Enhanced review" v={detail.kys.shipper_profile?.requires_enhanced_review ? 'YES' : 'no'} />
                </Section>

                <Section icon={Fingerprint} title="Fraud analytics scan">
                  <KV k="Verdict" v={detail.kys.fraud_summary?.verdict?.toUpperCase()} />
                  <KV k="Risk score" v={detail.kys.fraud_summary?.risk_score} />
                  <KV k="Providers used" v={detail.kys.fraud_summary?.providers_used?.join(', ') || 'none'} />
                  <KV k="Signals" v={detail.kys.fraud_summary?.signals?.join(', ') || 'none'} />
                  <KV k="Mocked" v={detail.kys.fraud_summary?.mocked ? 'YES (no keys)' : 'no'} />
                </Section>

                <Section icon={ShieldCheck} title={`Legal acceptances (${detail.legal_acceptances?.length || 0})`}>
                  {detail.legal_acceptances?.slice(0, 7).map(a => (
                    <div key={a.id} className="text-xs flex items-center justify-between py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <span>{a.doc_key.replaceAll('_', ' ')} · <span className="font-mono">{a.doc_version}</span></span>
                      <span className="text-slate-500">{new Date(a.accepted_at).toLocaleDateString()} · {a.ip_address}</span>
                    </div>
                  ))}
                </Section>

                {/* Action panel */}
                {detail.kys.status !== 'approved' && detail.kys.status !== 'rejected' && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-3" data-testid="detail-actions">
                    <div>
                      <label className="text-sm font-medium block mb-1.5">Reviewer notes (internal)</label>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
                        placeholder="Optional — visible to compliance only" data-testid="detail-notes" />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1.5">Rejection reason (required to reject)</label>
                      <input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
                        placeholder="e.g. OFAC hit on beneficial owner" data-testid="detail-reject-reason" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={doReject} disabled={busy} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="detail-reject">
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button onClick={doApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="detail-approve">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & provision ShipStation
                      </Button>
                    </div>
                  </div>
                )}

                {(detail.kys.status === 'approved' || detail.kys.status === 'rejected') && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-600 dark:text-slate-400" data-testid="detail-reviewed">
                    <p><b>Decision:</b> {detail.kys.status} on {detail.kys.reviewed_at?.slice(0, 19)}</p>
                    {detail.kys.reject_reason && <p className="mt-1"><b>Reason:</b> {detail.kys.reject_reason}</p>}
                    {detail.kys.reviewer_notes && <p className="mt-1"><b>Notes:</b> {detail.kys.reviewer_notes}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
      </div>
      <h3 className="font-semibold text-sm">{title}</h3>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="text-sm flex items-baseline gap-3">
    <span className="text-xs uppercase tracking-wide text-slate-400 min-w-[120px]">{k}</span>
    <span className="text-slate-800 dark:text-slate-200">{v || '—'}</span>
  </div>
);
